import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import "./CartContent.css";

const parsePrice = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== "string") return null;

  const cleanedValue = value.replace(/[^\d,.-]/g, "");
  if (!cleanedValue) return null;

  const lastComma = cleanedValue.lastIndexOf(",");
  const lastDot = cleanedValue.lastIndexOf(".");

  const normalizedValue =
    lastComma > lastDot
      ? cleanedValue.replace(/\./g, "").replace(",", ".")
      : cleanedValue.replace(/,/g, "");

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const getItemPrice = (item) =>
  parsePrice(
    item.priceEUR ??
      item.priceEuro ??
      item.priceNumberEuro ??
      item.unitPriceEUR ??
      item.price
  );

const formatCurrency = (value) =>
  new Intl.NumberFormat("bg-BG", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const Price = ({ amount, perUnit = false }) => (
  <span className="cart-price">
    <span className="cart-price__eur">
      {formatCurrency(amount)}
      {perUnit ? " / бр." : ""}
    </span>
  </span>
);

const CartContent = () => {
  const { cart, dispatch } = useCart();
  const navigate = useNavigate();

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [customer, setCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
  });

  const { totalItems, totalPrice, hasMissingPrice } = useMemo(
    () =>
      cart.reduce(
        (totals, item) => {
          const price = getItemPrice(item);
          const qty = Number(item.qty) || 0;

          return {
            totalItems: totals.totalItems + qty,
            totalPrice: totals.totalPrice + (price ?? 0) * qty,
            hasMissingPrice:
              totals.hasMissingPrice || price === null,
          };
        },
        {
          totalItems: 0,
          totalPrice: 0,
          hasMissingPrice: false,
        }
      ),
    [cart]
  );

  const handleChange = ({ target: { name, value } }) => {
    setCustomer((currentCustomer) => ({
      ...currentCustomer,
      [name]: value,
    }));
  };

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsTermsOpen(false);
    };

    if (isTermsOpen) {
      window.addEventListener("keydown", closeOnEscape);
    }

    return () => {
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isTermsOpen]);

  const updateQuantity = (item, quantity) => {
    dispatch({
      type: "UPDATE_QTY",
      id: item.id,
      qty: quantity,
    });
  };

  const removeItem = (item) => {
    dispatch({
      type: "REMOVE",
      id: item.id,
    });
  };

  const handleCheckout = async (event) => {
    event.preventDefault();

    if (!acceptedTerms) {
      alert("Моля, приемете Общите условия, за да завършите поръчката.");
      return;
    }

    if (cart.length === 0) {
      alert("Количката е празна.");
      return;
    }

    if (hasMissingPrice) {
      alert(
        "За един или повече продукти липсва цена. Моля, свържете се с нас."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const orderCart = cart.map((item) => ({
        ...item,
        priceEUR: getItemPrice(item),
        qty: Number(item.qty) || 0,
      }));

      const formData = new FormData();
      formData.append("name", customer.name.trim());
      formData.append("email", customer.email.trim());
      formData.append("phone", customer.phone.trim());
      formData.append("city", customer.city.trim());
      formData.append("address", customer.address.trim());
      formData.append("cart", JSON.stringify(orderCart));
      formData.append("totalEUR", totalPrice.toFixed(2));
      formData.append("total", totalPrice.toFixed(2));
      formData.append("currency", "EUR");

      const response = await fetch("/api/sendOrder.php", {
        method: "POST",
        body: formData,
      });

      let result;

      try {
        result = await response.json();
      } catch {
        throw new Error("Сървърът върна невалиден отговор.");
      }

      if (!response.ok || result.status !== "success") {
        throw new Error(
          result.msg || "Поръчката не можа да бъде изпратена."
        );
      }

      localStorage.setItem(
        "lastOrder",
        JSON.stringify({
          cart: orderCart,
          totalEUR: totalPrice,
          currency: "EUR",
        })
      );

      dispatch({ type: "CLEAR" });
      navigate("/thank-you");
    } catch (error) {
      console.error("Order submission failed:", error);
      alert(`Грешка при изпращане на поръчката: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <main className="cart-page">
        <section className="cart-shell cart-empty">
          <div className="cart-empty__icon" aria-hidden="true">
            🛒
          </div>

          <p className="cart-kicker">ВАШАТА КОЛИЧКА</p>
          <h1>Количката е празна</h1>
          <p>Все още не сте добавили продукти към поръчката си.</p>

          <button
            className="cart-button cart-button--blue"
            type="button"
            onClick={() => navigate(-1)}
          >
            Продължи към продуктите
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-shell">
        <header className="cart-hero">
          <div className="cart-hero__glow cart-hero__glow--one" />
          <div className="cart-hero__glow cart-hero__glow--two" />

          <div className="cart-hero__content">
            <p className="cart-kicker">ОНЛАЙН ПОРЪЧКА</p>
            <h1>Вашата количка</h1>
            <p>
              Проверете продуктите и въведете данните си, за да изпратите
              запитване за поръчка.
            </p>
          </div>
        </header>

        <form className="cart-layout" onSubmit={handleCheckout}>
          <div className="cart-main">
            <section aria-labelledby="cart-products-title">
              <div className="cart-section-heading">
                <div>
                  <p className="cart-step">СТЪПКА 1</p>
                  <h2 id="cart-products-title">Избрани продукти</h2>
                </div>

                <span className="cart-count">
                  {totalItems} {totalItems === 1 ? "продукт" : "продукта"}
                </span>
              </div>

              <ul className="cart-products">
                {cart.map((item) => {
                  const price = getItemPrice(item);

                  return (
                    <li className="cart-product" key={item.id}>
                      <div className="cart-product__image">
                        {item.image ? (
                          <img src={item.image} alt="" />
                        ) : (
                          <span aria-hidden="true">◈</span>
                        )}
                      </div>

                      <div className="cart-product__body">
                        <div className="cart-product__top">
                          <div>
                            <h3>{item.name}</h3>
                            {item.pack && <p>{item.pack}</p>}

                            <p>
                              {price === null ? (
                                "Цена при запитване"
                              ) : (
                                <Price amount={price} perUnit />
                              )}
                            </p>
                          </div>

                          <strong>
                            {price === null ? (
                              "—"
                            ) : (
                              <Price amount={price * item.qty} />
                            )}
                          </strong>
                        </div>

                        <div className="cart-product__bottom">
                          <div
                            className="quantity-control"
                            aria-label={`Количество за ${item.name}`}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(item, item.qty - 1)
                              }
                              disabled={item.qty <= 1}
                              aria-label="Намали количеството"
                            >
                              −
                            </button>

                            <span>{item.qty}</span>

                            <button
                              type="button"
                              onClick={() =>
                                updateQuantity(item, item.qty + 1)
                              }
                              aria-label="Увеличи количеството"
                            >
                              +
                            </button>
                          </div>

                          <button
                            className="cart-remove"
                            type="button"
                            onClick={() => removeItem(item)}
                          >
                            Премахни
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section
              className="cart-contact"
              aria-labelledby="cart-contact-title"
            >
              <div className="cart-section-heading">
                <div>
                  <p className="cart-step">СТЪПКА 2</p>
                  <h2 id="cart-contact-title">Данни за контакт</h2>
                </div>
              </div>

              <div className="cart-fields">
                <label>
                  Име и фамилия <b>*</b>
                  <input
                    name="name"
                    type="text"
                    autoComplete="name"
                    value={customer.name}
                    onChange={handleChange}
                    placeholder="Вашето име"
                    required
                  />
                </label>

                <label>
                  Имейл <b>*</b>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={customer.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    required
                  />
                </label>

                <label>
                  Телефон <b>*</b>
                  <input
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={customer.phone}
                    onChange={handleChange}
                    placeholder="+359 ..."
                    required
                  />
                </label>

                <label>
                  Град <b>*</b>
                  <input
                    name="city"
                    type="text"
                    autoComplete="address-level2"
                    value={customer.city}
                    onChange={handleChange}
                    placeholder="Например: Бургас"
                    required
                  />
                </label>

                <label>
                  Адрес <b>*</b>
                  <input
                    name="address"
                    type="text"
                    autoComplete="street-address"
                    value={customer.address}
                    onChange={handleChange}
                    placeholder="Улица, номер, вход, апартамент"
                    required
                  />
                </label>
              </div>
            </section>
          </div>

          <aside
            className="cart-summary"
            aria-label="Обобщение на поръчката"
          >
            <p className="cart-step">ОБОБЩЕНИЕ</p>
            <h2>Вашата поръчка</h2>

            <div className="cart-summary__rows">
              <div>
                <span>Продукти</span>
                <span>{totalItems} бр.</span>
              </div>

              <div className="cart-summary__total">
                <span>Обща сума</span>
                <strong>
                  {hasMissingPrice ? (
                    "При запитване"
                  ) : (
                    <Price amount={totalPrice} />
                  )}
                </strong>
              </div>
            </div>

            <p className="cart-summary__note">
              Всички цени са в евро (€). ДДС е включен, ако това е
              приложимо за посочените цени.
            </p>

            <label className="terms-checkbox">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(event) =>
                  setAcceptedTerms(event.target.checked)
                }
              />

              <span>
                Приемам{" "}
                <button
                  type="button"
                  className="terms-open-button"
                  onClick={() => setIsTermsOpen(true)}
                >
                  Общите условия
                </button>
                .
              </span>
            </label>

            <button
              className="cart-button cart-button--orange"
              type="submit"
              disabled={!acceptedTerms || isSubmitting}
            >
              {isSubmitting ? "Изпращане..." : "Потвърди поръчката"}
            </button>

            <p className="cart-summary__note">
              Ще се свържем с вас за потвърждение на поръчката.
            </p>
          </aside>
        </form>

        {isTermsOpen && (
          <div
            className="terms-modal-backdrop"
            role="presentation"
            onMouseDown={() => setIsTermsOpen(false)}
          >
            <section
              className="terms-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="terms-modal-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <header className="terms-modal__header">
                <div>
                  <p className="cart-step">ИНФОРМАЦИЯ</p>
                  <h2 id="terms-modal-title">Общи условия</h2>
                </div>

                <button
                  className="terms-modal__close"
                  type="button"
                  aria-label="Затвори общите условия"
                  onClick={() => setIsTermsOpen(false)}
                >
                  ×
                </button>
              </header>

              <div className="terms-modal__body">
                <iframe title="Общи условия" src="/terms" />
              </div>

              <footer className="terms-modal__footer">
                <button
                  className="cart-button cart-button--blue"
                  type="button"
                  onClick={() => setIsTermsOpen(false)}
                >
                  Затвори
                </button>
              </footer>
            </section>
          </div>
        )}
      </div>
    </main>
  );
};

export default CartContent;